import React, { useState } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { FileIcon } from './attachments/FileIcon';
import { formatFileSize } from './attachments/fileIconUtils';
import { Button } from './ui';

export interface FileAttachmentItem {
  id: number;
  fileName?: string | null;
  filePath?: string | null;
  fileType?: string | null;
  fileSize?: number | null;
  uploadedAt?: string | null;
}

interface EntityAttachmentsSectionProps {
  downloadEndpoint: string;
  files: FileAttachmentItem[];
  defaultDownloadName?: string;
  /** Returns the API path to download a single file (e.g. `/crm-tasks/tasks/123/files/456`). When set, clicking a file panel downloads that file. */
  getViewFileUrl?: (fileId: number) => string;
}

export const EntityAttachmentsSection: React.FC<EntityAttachmentsSectionProps> = ({
  downloadEndpoint,
  files,
  defaultDownloadName = 'files.zip',
  getViewFileUrl,
}) => {
  const { colors } = useTheme();
  const [downloading, setDownloading] = useState(false);
  const [viewingId, setViewingId] = useState<number | null>(null);

  if (!files || files.length === 0) {
    return (
      <div
        className={cn(
          'rounded-xl border-2 border-dashed py-8 px-6 text-center',
          colors.border,
          colors.surfaceHover
        )}
      >
        <div className="flex justify-center mb-3">
          <div
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center',
              colors.surfaceHover,
              colors.textMuted
            )}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </div>
        </div>
        <p className={cn('text-sm font-medium', colors.text)}>No attachments</p>
        <p className={cn('text-xs mt-0.5', colors.textMuted)}>Files added to this record will appear here</p>
      </div>
    );
  }

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const response = await api.get(downloadEndpoint, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const fileName = contentDisposition
        ? decodeURIComponent(contentDisposition.split('filename=')[1]?.replace(/"/g, '') || defaultDownloadName)
        : defaultDownloadName;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadFile = async (file: FileAttachmentItem) => {
    if (!getViewFileUrl) return;
    setViewingId(file.id);
    try {
      const url = getViewFileUrl(file.id);
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const contentDisposition = response.headers['content-disposition'];
      const fileName = contentDisposition
        ? decodeURIComponent(contentDisposition.split('filename=')[1]?.replace(/"/g, '') || '')
        : file.fileName || 'download';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setViewingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {files.map((file) => (
          <div
            key={file.id}
            role={getViewFileUrl ? 'button' : undefined}
            tabIndex={getViewFileUrl ? 0 : undefined}
            onClick={getViewFileUrl ? () => handleDownloadFile(file) : undefined}
            onKeyDown={
              getViewFileUrl
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDownloadFile(file);
                    }
                  }
                : undefined
            }
            className={cn(
              'flex items-center gap-3 min-w-0 flex-1 rounded-xl border py-3 px-4 shadow-sm transition-shadow',
              colors.border,
              colors.surface,
              getViewFileUrl && 'cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400/50 dark:focus:ring-gray-500/50',
              getViewFileUrl && colors.interactiveHover
            )}
            title={getViewFileUrl ? 'Click to download file' : undefined}
          >
            <FileIcon fileName={file.fileName} size="md" />
            <div className="min-w-0 flex-1">
              <p
                title={file.fileName || ''}
                className={cn('text-sm font-medium truncate', colors.text)}
              >
                {file.fileName || 'Unnamed file'}
              </p>
              {file.fileSize != null && (
                <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                  {formatFileSize(file.fileSize)}
                </p>
              )}
            </div>
            {getViewFileUrl && viewingId === file.id && (
              <span className={cn('flex-shrink-0', colors.primaryFg)} aria-hidden>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </span>
            )}
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDownloadAll}
        disabled={downloading}
        className="inline-flex items-center gap-2"
      >
        {downloading ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Downloading…
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {files.length === 1 ? 'Download file' : 'Download all as ZIP'}
          </>
        )}
      </Button>
    </div>
  );
};

export default EntityAttachmentsSection;
